import React, { memo, useMemo, type CSSProperties } from 'react';
import type { HeaderBar as HeaderBarType, StepNode, TapTarget } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import { resolveAssetReference, resolveLabel } from '../../../utils/contentResolver';
import type { OnActionCallback } from '../../../types/events';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { mapEdgeInsetsToCSS, mapVerticalAlignment, mapHorizontalAlignment, mapImageStyles } from '../../../utils/styleMapper';
import { Text } from '../Text';
import { Button } from '../Button';
import { PageIndicator } from '../PageIndicator';
import { DashesPageIndicator } from '../DashesPageIndicator';
import { useElementAnimation } from '../../../animations';
import { headerBarRenders, normalizeHeaderProgressIndicator } from '../../../utils/renderPredicates';
import styles from './HeaderBar.module.css';

const SCALE_MODE_MAP: Record<string, CSSProperties['objectFit']> = {
  scaleToFill: 'fill',
  scaleAspectFit: 'contain',
  scaleAspectFill: 'cover',
  center: 'none',
  top: 'none',
  bottom: 'none',
  left: 'none',
  right: 'none',
};

const SCALE_POSITION_MAP: Record<string, string> = {
  center: 'center center',
  top: 'top center',
  bottom: 'bottom center',
  left: 'center left',
  right: 'center right',
};

export interface HeaderBarProps {
  header: HeaderBarType | undefined;
  locale: string;
  variables?: TemplateVariables;
  step: StepNode;
  onAction?: OnActionCallback;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  absolute?: boolean;
}

export const HeaderBar = memo(function HeaderBar({
  header,
  locale,
  variables,
  step,
  onAction,
  className,
  'data-testid': testId,
  editPath,
  absolute = false,
}: HeaderBarProps): React.ReactElement | null {
  const headerAnim = useElementAnimation(editPath);

  // ⚠️ Rules of Hooks: every hook must run on EVERY render, so these useMemo
  // calls live ABOVE the conditional early-returns below. A theme cascade can
  // materialize header members mid-session (e.g. progressIndicator.activeColor
  // written onto an empty header), flipping the early-return branch — hooks
  // placed after it crash with "Rendered more hooks than during the previous
  // render" and white-screen the app.
  const appearance = (header as any)?.appearance;
  const insets = (header as any)?.insets;
  const progressLayout = (header as any)?.progressLayout;

  const containerStyle = useMemo((): CSSProperties => {
    const s: CSSProperties = {};
    if (appearance?.height != null) s.minHeight = appearance.height;
    if (appearance?.backgroundColor) s.backgroundColor = appearance.backgroundColor;
    if (appearance?.opacity != null) s.opacity = appearance.opacity;
    if (insets) Object.assign(s, mapEdgeInsetsToCSS(insets));
    return s;
  }, [appearance, insets]);

  const progressStyle = useMemo((): CSSProperties => {
    const s: CSSProperties = {};
    s.alignSelf = mapVerticalAlignment(progressLayout?.verticalAlignment) as string;
    if (progressLayout?.insets) Object.assign(s, mapEdgeInsetsToCSS(progressLayout.insets));
    return s;
  }, [progressLayout]);

  // Renders nothing without a back / skip / close button or a progress indicator (shared with the
  // entrance plan: utils/renderPredicates.ts)
  if (!headerBarRenders(header)) {
    return null;
  }

  const { back, skip, close } = header;
  // Normalize progressIndicator: handle raw numbers (legacy), missing value, and old schema names
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const progressIndicator = normalizeHeaderProgressIndicator(header) as any;

  // Mirrors Button's null-return logic: returns true only when Button would render
  const willButtonRender = (t: TapTarget | undefined): boolean => {
    if (!t) return false;
    if (t.kind === 'icon') return true;
    // Label kind: resolveLabel returns null when no translations or locale missing
    return resolveLabel(t.content as any, locale, variables) !== null;
  };

  const isFullWidth = progressLayout?.fullWidth === true;
  const respectButton = progressIndicator?.respectButton !== false; // default true
  const hasLeftButton = willButtonRender(back) || willButtonRender(close);
  const hasRightButton = willButtonRender(skip);

  const isDashesKind = (kind: string | undefined): boolean => {
    return kind === 'segmented' || kind === 'segmentedTitle' || kind === 'segmentedIcon' || kind === 'segmentedTitleIcon';
  };

  // Render just the bar/dashes visualization (no image/title — those are universal)
  const renderIndicatorBar = () => {
    if (!progressIndicator) return null;

    const kind = progressIndicator.kind || 'linearBar';

    if (isDashesKind(kind)) {
      return (
        <DashesPageIndicator
          indicator={progressIndicator}
          data-testid={testId ? `${testId}-page-indicator` : undefined}
          editPath={editPath ? `${editPath}.progressIndicator` : undefined}
        />
      );
    }

    return (
      <PageIndicator
        indicator={progressIndicator}
        data-testid={testId ? `${testId}-page-indicator` : undefined}
        editPath={editPath ? `${editPath}.progressIndicator` : undefined}
      />
    );
  };

  // Map iconPlacement to flex-direction for the logo container
  const mapIconPlacementToFlex = (placement?: string): CSSProperties => {
    switch (placement) {
      case 'end':
      case 'fixedEnd':
        return { flexDirection: 'row-reverse' };
      case 'top':
        return { flexDirection: 'column' };
      case 'bottom':
        return { flexDirection: 'column-reverse' };
      default: // 'start', 'fixedStart', undefined
        return { flexDirection: 'row' };
    }
  };

  // Universal logo row: icon + title side-by-side (available for any indicator kind)
  const renderLogoRow = () => {
    const hasIcon = !!progressIndicator?.icon;
    const hasTitle = !!progressIndicator?.title;
    if (!hasIcon && !hasTitle) return null;

    const iconUrl = hasIcon
      ? resolveAssetReference(progressIndicator!.icon!, locale, 'original')
      : null;

    const containerStyle: CSSProperties = {
      ...mapIconPlacementToFlex(progressIndicator?.iconPlacement),
    };
    if (progressIndicator?.contentAlignment) {
      containerStyle.justifyContent = mapHorizontalAlignment(progressIndicator.contentAlignment) as string;
    }

    const iconAppearance = progressIndicator?.icon?.appearance;
    const iconScaleMode = iconAppearance?.scaleMode;
    const iconInsets = progressIndicator?.iconInsets;

    // Use same style mapping as Image component
    const iconStyles = mapImageStyles(iconAppearance);
    const objectFit = iconScaleMode ? SCALE_MODE_MAP[iconScaleMode] : undefined;
    const objectPosition = iconScaleMode ? SCALE_POSITION_MAP[iconScaleMode] : undefined;

    const imgStyle: CSSProperties = {
      ...iconStyles,
      ...(objectFit && { objectFit }),
      ...(objectPosition && { objectPosition }),
    };

    return (
      <div
        className={styles.logoContainer}
        style={containerStyle}
        data-edit-path={editPath ? `${editPath}.progressIndicator` : undefined}
      >
        {iconUrl && (
          <div className={styles.logoImage} style={iconInsets ? mapEdgeInsetsToCSS(iconInsets) : undefined}>
            <img
              src={iconUrl}
              alt=""
              style={imgStyle}
            />
          </div>
        )}
        {hasTitle && (
          <Text
            label={progressIndicator!.title!}
            locale={locale}
            variables={variables}
            className={styles.progressTitle}
            // A granular title alignment needs an actual alignment box. Keep
            // the legacy shrink-to-content behavior when align is absent so
            // progressIndicator.contentAlignment continues to position the
            // whole icon/title group exactly as before.
            style={progressIndicator!.title!.appearance?.align
              ? { width: 'auto', flex: 1, minWidth: 0 }
              : { width: 'auto' }}
          />
        )}
      </div>
    );
  };

  const navClassName = [
    styles.headerBar,
    absolute && styles.absolute,
    isFullWidth && styles.headerBarFullWidth,
    className,
    headerAnim.className,
  ].filter(Boolean).join(' ');

  const navStyle = headerAnim.style ? { ...containerStyle, ...headerAnim.style } : containerStyle;

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  const logoRow = renderLogoRow();
  const indicatorBar = renderIndicatorBar();

  // The progress group: logo row (image+title) + indicator, positioned by verticalAlignment
  const renderProgressGroup = () => {
    if (!logoRow && !indicatorBar) return null;
    return (
      <div className={styles.progressGroup} style={progressStyle}>
        {logoRow}
        {indicatorBar}
      </div>
    );
  };

  const renderButtons = () => (
    <>
      <div className={styles.left}>
        {close && (
          <Button
            tapTarget={close}
            locale={locale}
            variables={variables}
            step={step}
            onAction={onAction}
            editPath={editPath ? `${editPath}.close` : undefined}
          />
        )}
        {back && (
          <Button
            tapTarget={back}
            locale={locale}
            variables={variables}
            step={step}
            onAction={onAction}
            editPath={editPath ? `${editPath}.back` : undefined}
          />
        )}
      </div>

      <div className={styles.center}>
        {!isFullWidth && renderProgressGroup()}
      </div>

      <div className={styles.right}>
        {skip && (
          <Button
            tapTarget={skip}
            locale={locale}
            variables={variables}
            step={step}
            onAction={onAction}
            editPath={editPath ? `${editPath}.skip` : undefined}
          />
        )}
      </div>
    </>
  );

  if (isFullWidth) {
    return (
      <nav
        className={navClassName}
        style={navStyle}
        data-anim={headerAnim['data-anim']}
        data-testid={schemaTestId}
        data-edit-path={editPath}
      >
        {renderButtons()}

        {(logoRow || indicatorBar) && (
          <div className={styles.progressOverlay}>
            {respectButton && hasLeftButton && <div className={styles.left} />}
            <div className={styles.progressOverlayContent} style={progressStyle}>
              {logoRow}
              {indicatorBar}
            </div>
            {respectButton && hasRightButton && <div className={styles.right} />}
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav
      className={navClassName}
      style={navStyle}
      data-anim={headerAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {renderButtons()}
    </nav>
  );
});

HeaderBar.displayName = 'HeaderBar';
